import { useMemo, useState } from "react"
import { Button } from "@/components/Button"
import { buildEmailSrcDoc, looksLikeHtml } from "@/lib/emailBody"

const MIN_HEIGHT = 80
const MAX_HEIGHT = 1600

function RawBody({ body }: { body: string }) {
  return (
    <pre className="whitespace-pre-wrap break-words font-sans text-sm text-primary">{body}</pre>
  )
}

/**
 * A message body. Plain text renders in a <pre>; HTML-only emails (ingest stores raw markup in
 * `body_text` when there is no text part) render in a script-less sandboxed iframe with a
 * restrictive CSP. Remote images stay blocked until asked for so tracking pixels don't fire
 * on open.
 */
export function EmailBody({ body }: { body: string | null }) {
  const text = body || ""
  const html = text !== "" && looksLikeHtml(text)
  const [allowRemoteImages, setAllowRemoteImages] = useState(false)
  const [showSource, setShowSource] = useState(false)
  const [height, setHeight] = useState(MIN_HEIGHT)

  const srcDoc = useMemo(
    () => (html ? buildEmailSrcDoc(text, { allowRemoteImages }) : ""),
    [html, text, allowRemoteImages]
  )

  if (!html) return <RawBody body={text || "(no body captured)"} />

  return (
    <div>
      {showSource ? (
        <RawBody body={text} />
      ) : (
        <iframe
          title="Email body"
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          srcDoc={srcDoc}
          className="w-full rounded-md border border-border bg-white"
          style={{ height }}
          onLoad={(e) => {
            // Same-origin (allow-same-origin) and script-less, so reading the document is safe.
            const scrollHeight = e.currentTarget.contentDocument?.documentElement.scrollHeight
            if (scrollHeight) setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, scrollHeight)))
          }}
        />
      )}
      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        {!showSource && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => setAllowRemoteImages((v) => !v)}
          >
            {allowRemoteImages ? "Hide images" : "Load images"}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => setShowSource((v) => !v)}
        >
          {showSource ? "View rendered" : "View source"}
        </Button>
      </div>
    </div>
  )
}
